import { ui_service_ip, intelligence_ip } from "./ip_details";
import { call_post_api, call_get_api, call_delete_api, call_update_api } from "./event_handling";
import { getCompleteEndpoint, getProtocol, checkExpiryPowerBI } from "./common_utils";

/**
 * Generic function to handle API calls based on the method type.
 * @param {string} method - The HTTP method (GET, POST, UPDATE, DELETE).
 * @param {string} endpoint - The endpoint for the API call.
 * @param {Object} params - The parameters or request body for the API call.
 * @returns {Object} - The result from the API call.
 */
async function handleApiCall(method, endpoint, params) {
    try {
        let result;
        switch (method.toUpperCase()) {
            case "GET":
                result = await call_get_api(ui_service_ip, endpoint, params);
                break;

            case "POST":
                result = await call_post_api(ui_service_ip, endpoint, params);
                break;

            case "PUT":
            case "UPDATE":
                result = await call_update_api(ui_service_ip, endpoint, params);
                break;

            case "DELETE":
                result = await call_delete_api(ui_service_ip, endpoint, params);
                break;

            default:
                throw new Error(`Unsupported HTTP method: ${method}`);
        }

        return result; // Return the result from the API
    } catch (error) {
        console.error(`Error during ${method} request to ${endpoint}:`, error); // Log any errors
    }
}

/**
 * Function to fetch the latest environment list (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getLatestEnvironmentList(requestParams) {
    return handleApiCall("GET", "get_environments_latest", requestParams);
}

/**
 * Function to fetch the watchlist (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getAIAssitance(requestParams) {
    return handleApiCall("POST", "query-data", requestParams);
}

function getSessionAuthHeaders() {
    let session = localStorage.getItem("sessionToken");
    const userId = localStorage.getItem("user_id");
    const teamId = localStorage.getItem("team_id");

    if (session !== "" && session !== null && session !== undefined) {
        const item = JSON.parse(session);
        const now = new Date();
        if (now.getTime() > item.expiry) {
            if (checkExpiryPowerBI() === false) {
                localStorage.removeItem("sessionToken");
                window.location = "/";
            }
        }
        session = item.value;
    }

    return {
        session,
        userId,
        teamId,
    };
}

function normalizeAssistantAnswer(response) {
    if (!response) {
        return {
            answer_type: "description",
            value: "I couldn't find a matching answer for that question. Please try one of the suggested prompts.",
        };
    }

    if (
        response.type === "error" ||
        Number(response.code) === 403 ||
        (response.code != null &&
            Number(response.code) !== 200 &&
            response.result == null &&
            response.answer == null &&
            !response.answer_type)
    ) {
        return {
            answer_type: "description",
            value: response.message === "Token Missing"
                ? "Authentication token was missing on the assistant request. Please refresh and sign in again."
                : (response.message || "Something went wrong while getting a response. Please try again."),
        };
    }

    // Backend process_question_stream final chunk: { type:"final", code, message, result }
    // Legacy / alternate: { answer } or answer object itself
    let payload = response.result !== undefined ? response.result : response.answer;

    if (payload == null && response.answer_type) {
        return response;
    }

    if (typeof payload === "string") {
        return {
            answer_type: "description",
            starting_context: "",
            value: payload,
            ending_context: "",
        };
    }

    if (payload && typeof payload === "object") {
        if (payload.answer_type) {
            return payload;
        }

        // Graph / hybrid agents: { question, answer }
        if (payload.answer != null) {
            if (typeof payload.answer === "string") {
                return {
                    answer_type: "description",
                    starting_context: "",
                    value: payload.answer,
                    ending_context: "",
                };
            }
            if (payload.answer && payload.answer.answer_type) {
                return payload.answer;
            }
        }

        if (payload.value !== undefined) {
            return {
                answer_type: payload.answer_type || "description",
                starting_context: payload.starting_context || "",
                value: payload.value,
                ending_context: payload.ending_context || "",
            };
        }
    }

    return {
        answer_type: "description",
        value: "I couldn't find a matching answer for that question. Please try one of the suggested prompts.",
    };
}

function handleAssistantStreamEvent(event, onStatus) {
    if (!event || typeof event !== "object") {
        return null;
    }

    // Status: { type:"status", stage, message }
    if (event.type === "status") {
        if (typeof onStatus === "function") {
            onStatus(event.message || event.stage || "Thinking...");
        }
        return null;
    }

    // Error: { type:"error", code, message }
    if (event.type === "error") {
        return event;
    }

    // Final: { type:"final", code, message, result }
    if (event.type === "final" || event.result !== undefined || event.answer || event.answer_type) {
        return event;
    }

    return null;
}

async function consumeAssistantNdjsonStream(response, onStatus) {
    const reader = response.body && response.body.getReader
        ? response.body.getReader()
        : null;

    if (!reader) {
        const fallback = await response.json();
        return fallback;
    }

    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let finalEvent = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (let i = 0; i < lines.length; i += 1) {
            const line = lines[i].trim();
            if (!line) {
                continue;
            }

            let event;
            try {
                event = JSON.parse(line);
            } catch (err) {
                console.error("Failed to parse assistant stream chunk:", line, err);
                continue;
            }

            const maybeFinal = handleAssistantStreamEvent(event, onStatus);
            if (maybeFinal) {
                finalEvent = maybeFinal;
                if (event.type === "error" || event.type === "final") {
                    // Keep reading until stream ends, but remember last final/error.
                }
            }
        }
    }

    const trailing = buffer.trim();
    if (trailing) {
        try {
            const event = JSON.parse(trailing);
            const maybeFinal = handleAssistantStreamEvent(event, onStatus);
            if (maybeFinal) {
                finalEvent = maybeFinal;
            }
        } catch (err) {
            console.error("Failed to parse trailing assistant stream chunk:", trailing, err);
        }
    }

    return finalEvent;
}

/**
 * Query AI assistant via intelligence service (GET, NDJSON stream).
 * Matches backend process_question_stream:
 *   - status: { type:"status", stage, message }
 *   - final:  { type:"final", code, message, result }
 *   - error:  { type:"error", code, message }
 * Payload goes in the "data" header; authToken/userId/teamId are also headers.
 * @param {Object} requestParams - tenant_id, user_id, question, type
 * @param {Object} options - { onStatus(message) } for live progress updates
 * @returns {Object} - answer object { answer_type, value, ... }
 */
export async function askQuestionAI(requestParams, options = {}) {
    const { onStatus } = options;
    const { session, userId, teamId } = getSessionAuthHeaders();

    // Backend returns {"code":403,"message":"Token Missing"} when any of these are absent.
    if (!session || !userId || !teamId) {
        return {
            answer_type: "description",
            value: "Your session is missing or expired. Please sign in again to use Qualdo Assistant.",
        };
    }

    const payload = {
        ...(requestParams || {}),
        tenant_id: (requestParams && requestParams.tenant_id) || teamId,
        user_id: (requestParams && requestParams.user_id) || userId,
    };

    const protocol = getProtocol(intelligence_ip);
    const reqEndPoint = getCompleteEndpoint(protocol, intelligence_ip, "assistant/query");

    if (typeof onStatus === "function") {
        onStatus("Understanding your question...");
    }

    const response = await fetch(reqEndPoint, {
        method: "GET",
        headers: {
            data: JSON.stringify(payload),
            "Content-Type": "application/json",
            "api-type": "qualdo_db_auth",
            authToken: session,
            userId: userId,
            teamId: teamId,
        },
    });

    const contentType = (response.headers.get("content-type") || "").toLowerCase();
    const isLegacyJson = contentType.includes("application/json") && !contentType.includes("ndjson");

    // Streamed NDJSON from process_question_stream. Fall back to single JSON for auth/error payloads.
    if (!isLegacyJson) {
        const finalEvent = await consumeAssistantNdjsonStream(response, onStatus);
        return normalizeAssistantAnswer(finalEvent);
    }

    // Legacy single JSON response fallback
    const legacy = await response.json();
    return normalizeAssistantAnswer(legacy);
}

function pickRandomQuestions(questions, count = 3) {
    const pool = Array.isArray(questions) ? [...questions] : [];
    for (let i = pool.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = pool[i];
        pool[i] = pool[j];
        pool[j] = tmp;
    }
    return pool.slice(0, Math.min(count, pool.length));
}

export { pickRandomQuestions };

/**
 * Fetch recommended prompt questions by tab (dataset / pipeline / incident).
 * @param {Object} requestParams - tenant_id, user_id
 * @returns {Object} - { code, dataset_question, pipeline_question, incident_question }
 */
export function getRecommentationQuestions(requestParams) {
    return handleApiCall("GET", "get-recommendation-questions", requestParams);
}
